import { useNavigate } from '@tanstack/react-router';
import { Button, TextFieldPassword } from '@yembo/yemblocks-core';
import { isValidEmail } from '@yembo/yemblocks-core/dist/_util';
import { TextInput } from '@yembo/yemblocks-core/dist/TextInput';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { putLogin } from 'src/queries/Login';
import { useSubmitOnEnterKey } from 'src/react/_hooks/useEnterKeySubmit';
import { getCurrentTimezone } from 'src/react/_util/dates/timezone';
import { ImportanceNotice } from 'src/react/Authentication/Account/elements/ImportanceNotice';
import { LoginFormData } from 'src/react/Authentication/LoginMfa';
import { isProdGovEnvironment } from 'src/util/env';
import { loginDevice, notifyLockedAccount } from '../_util/common';
import { SectionHeader } from '../SectionHeader';

type Props = {
  formData: LoginFormData;
  setFormData: Dispatch<SetStateAction<LoginFormData>>;
};

export const LoginForm = ({ formData, setFormData }: Props) => {
  const [isNoticeSubmitted, setIsNoticeSubmitted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const onClickLogin = useCallback(async () => {
    const loginData = {
      email: formData.email ?? '',
      password: formData.password ?? '',
      timezone: getCurrentTimezone(),
    };

    setIsLoading(true);

    try {
      const loginResponse = await putLogin(loginData);

      if ('device' in loginResponse) {
        loginDevice(loginResponse);
      } else if ('employee' in loginResponse) {
        setFormData((data) => ({
          ...data,
          email: loginResponse.employee.email,
          mfaEmployee: loginResponse.employee,
        }));
        // biome-ignore lint/suspicious/noExplicitAny: router constraint bypass
        navigate({ to: '/verify-mfa', search: true } as any);
      }
    } catch (error) {
      notifyLockedAccount(error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [formData.email, formData.password, navigate, setFormData]);

  useSubmitOnEnterKey({ onSubmit: onClickLogin });

  return (
    <div className='login-form authentication-form'>
      {!isNoticeSubmitted && isProdGovEnvironment ? (
        <ImportanceNotice onSubmit={() => setIsNoticeSubmitted(true)} />
      ) : (
        <>
          <SectionHeader
            heading='Welcome back'
            caption='Please sign in below'
            showError={isError}
            errorMessage='Invalid email or password'
          />
          <div className='form-controls-wrapper'>
            <TextInput
              name='email'
              label='Email'
              value={formData.email}
              onChange={({ currentTarget: { value } }) => {
                const email = value.replaceAll(' ', '');
                setFormData((data) => ({
                  ...data,
                  email,
                  isEmailValid: isValidEmail(email),
                }));
              }}
              inputMode='email'
              checkValidity={() => (formData.isEmailValid ? undefined : 'Invalid email')}
            />
            <TextFieldPassword
              name='password'
              label='Password'
              onChange={({ currentTarget: { value } }) => setFormData((data) => ({ ...data, password: value }))}
            />
            <button
              type='button'
              className='reset-password-link'
              disabled={isLoading}
              onClick={() => navigate({ to: '/forgot-password' })}
            >
              Reset password
            </button>
          </div>
          <Button
            text='Login'
            onClick={onClickLogin}
            isLoading={isLoading}
            isDisabled={!formData.email || !formData.password || !formData.isEmailValid}
          />
        </>
      )}
    </div>
  );
};
